import type { AnnotationData, Skeleton } from '@/modules/Editor/AnnotationData'
import { CallbackStatus } from '@/modules/Editor/callbackHandler'
import { EditorCursor, selectCursor } from '@/modules/Editor/editorCursor'
import { isLeftMouseButton } from '@/modules/Editor/mouse'
import type { IPoint } from '@/modules/Editor/point'
import { createEditablePoint } from '@/modules/Editor/point'
import { ToolName } from '@/modules/Editor/tools/types'
import type { PointerEvent } from '@/core/utils/touch'
import { resolveEventPoint } from '@/core/utils/touch'
import { drawTemporarySkeletonV2 } from '@/modules/Editor/graphicsV2/drawTemporarySkeleton'
import { drawTemporarySkeletonPointByPoint } from '@/modules/Editor/graphicsV2/drawTemporarySkeletonPointByPoint'
import { drawGuideLines } from '@/modules/Editor/graphicsV2/drawGuideLines'
import type { Tool, ToolContext } from '@/modules/Editor/managers/toolManager'
import type { Action } from '@/modules/Editor/managers/actionManager'
import { setupMouseButtonLoadout } from '@/modules/Editor/plugins/mixins/loadouts'
import { preselectOrPromptForAnnotationClass } from '@/modules/Editor/utils/preselectOrPromptForAnnotationClass'
import type { View } from '@/modules/Editor/views/view'
import { setContext } from '@/services/sentry'
import { ToolEvents, EditorEvents } from '@/modules/Editor/eventBus'
import { useSkeletonToolState } from '@/modules/Editor/tools/skeleton/useSkeletonToolState'

import { annotationType } from './consts'
import { SkeletonPointByPointManager } from './SkeletonPointByPointManager'
// eslint-disable-next-line boundaries/element-types
import { useFeatureFlagsStore } from '@/pinia/useFeatureFlagsStore'

const annotationCreationAction = async (
  context: ToolContext,
  skeleton: AnnotationData,
  classId: number,
): Promise<Action> => {
  const params = { type: annotationType, data: skeleton, classId: classId }

  const newAnnotation =
    await context.editor.activeView.annotationManager.prepareAnnotationForCreation(params)

  if (!newAnnotation) {
    throw new Error('Failed to prepare annotation for creation')
  }

  // support view id for undoing purposes
  const sourceViewId = context.editor.activeView.id

  return {
    do(): boolean {
      context.editor.activeView.annotationManager.createAnnotation(newAnnotation)
      context.editor.activeView.annotationManager.selectAnnotations([newAnnotation.id])

      context.editor.activeView.measureManager.updateOverlayForExistingAnnotation(newAnnotation)

      return true
    },
    undo(): boolean {
      // we don't want to use the active view here, as by the time the user want to undo
      // the view could have changed due to multi-slot;
      // this would result in the annotation not being found and the undo action failing
      const sourceView = context.editor.viewsList.find(({ id }) => id === sourceViewId)
      if (!sourceView) {
        return false
      }

      sourceView.annotationManager.deleteAnnotation(newAnnotation.id)

      sourceView.measureManager.updateOverlayForExistingAnnotation(newAnnotation)
      return true
    },
  }
}

export interface SkeletonTool extends Tool {
  initialPoint?: IPoint
  cursorPoint?: IPoint
  pointByPointManager?: SkeletonPointByPointManager
  isPointByPointMode?: boolean
  isBackspacePressed?: boolean
  lastKnownSkeletonMetadata?: string

  onStart: (context: ToolContext, event: PointerEvent) => void
  onMove: (context: ToolContext, event: PointerEvent) => void
  onEnd: (context: ToolContext, event: PointerEvent) => void
  draw: (view: View) => void
  savePointByPointSkeleton: (context: ToolContext) => Promise<void>
  switchMode: (context: ToolContext) => void
  checkForClassMetadataChanges: (view: View) => void
}

export const skeletonTool: SkeletonTool = {
  initialPoint: undefined,
  cursorPoint: undefined,
  pointByPointManager: undefined,
  isPointByPointMode: false,
  isBackspacePressed: false,
  lastKnownSkeletonMetadata: undefined,

  onStart(context: ToolContext, event: PointerEvent) {
    const point = resolveEventPoint(event)
    if (!point) {
      return
    }

    if (this.isPointByPointMode && this.pointByPointManager) {
      const imagePoint = context.editor.activeView.camera.canvasViewToImageView(point)
      const isObscured = this.isBackspacePressed || undefined

      this.pointByPointManager.placeNode(imagePoint, isObscured)
      this.draw(context.editor.activeView)

      return
    }

    this.initialPoint = point
    this.draw(context.editor.activeView)
  },

  onMove(context: ToolContext, event: PointerEvent) {
    if (!context.editor.activeView.hitTarget(event)) {
      return CallbackStatus.Stop
    }

    const point = resolveEventPoint(event)
    if (!point) {
      return
    }

    this.cursorPoint = point
    this.draw(context.editor.activeView)

    if (this.isPointByPointMode) {
      return CallbackStatus.Stop
    }

    if (!this.initialPoint) {
      return
    }
    return CallbackStatus.Stop
  },

  onEnd(context: ToolContext) {
    if (this.isPointByPointMode) return

    this.reset(context)
    this.draw(context.editor.activeView)
  },

  async savePointByPointSkeleton(context: ToolContext) {
    if (!this.pointByPointManager) {
      return
    }

    const klass = context.editor.activeView.annotationManager.preselectedAnnotationClass
    if (!klass) return

    try {
      const skeletonData = this.pointByPointManager.toSkeletonAnnotation()
      const savedNodesCount = skeletonData.nodes.length

      const skeleton: Skeleton = {
        nodes: skeletonData.nodes.map((node) => ({
          ...node,
          point: createEditablePoint(node.point),
        })),
      }

      await context.editor.actionManager.do(
        await annotationCreationAction(context, skeleton, klass.id),
      )

      const skeletonToolState = useSkeletonToolState()
      skeletonToolState.showSuccessFeedback(savedNodesCount)
    } catch (e) {
      setContext('error', { error: e })
      console.error('Point-by-point skeleton tool createAnnotation failed')
    }

    this.reset(context)
    this.draw(context.editor.activeView)
  },

  switchMode(context: ToolContext) {
    const klass = context.editor.activeView.annotationManager.preselectedAnnotationClass
    const featureFlagsStore = useFeatureFlagsStore()

    const isFeatureFlagEnabled = featureFlagsStore.featureFlags.POINT_BY_POINT_SKELETON
    const shouldBePointByPoint =
      isFeatureFlagEnabled && klass?.skeletonMetaData?.mode === 'point_by_point'
    const currentClassId = klass?.id
    const previousClassId = this.pointByPointManager?.getClassId() || null

    this.cursorPoint = undefined
    context.editor.activeView.annotationsLayer.draw()

    if (shouldBePointByPoint && !this.isPointByPointMode) {
      this.isPointByPointMode = true

      if (klass) {
        this.pointByPointManager = new SkeletonPointByPointManager(klass)
        this.lastKnownSkeletonMetadata = JSON.stringify(klass.skeletonMetaData)

        const skeletonToolState = useSkeletonToolState()
        if (this.pointByPointManager) {
          skeletonToolState.initializeManager(this.pointByPointManager)
        }

        ToolEvents.changed.emit({
          toolName: ToolName.Skeleton,
          lastHotkeyPressed: null,
          editing: false,
        })
      }

      selectCursor(EditorCursor.Draw)
    } else if (!shouldBePointByPoint && this.isPointByPointMode) {
      this.isPointByPointMode = false

      if (this.pointByPointManager) {
        const skeletonToolState = useSkeletonToolState()
        skeletonToolState.cleanup()
        this.pointByPointManager = undefined
        this.lastKnownSkeletonMetadata = undefined

        ToolEvents.cancel.emit({
          toolName: ToolName.Skeleton,
          lastHotkeyPressed: null,
          editing: false,
        })
      }

      selectCursor(EditorCursor.BBox)
    } else if (
      shouldBePointByPoint &&
      this.isPointByPointMode &&
      currentClassId !== previousClassId
    ) {
      if (klass) {
        this.pointByPointManager = new SkeletonPointByPointManager(klass)
        this.lastKnownSkeletonMetadata = JSON.stringify(klass.skeletonMetaData)

        const skeletonToolState = useSkeletonToolState()
        if (this.pointByPointManager) {
          skeletonToolState.initializeManager(this.pointByPointManager)
        }

        ToolEvents.changed.emit({
          toolName: ToolName.Skeleton,
          lastHotkeyPressed: null,
          editing: false,
        })
      }
    }

    this.reset(context)
    this.draw(context.editor.activeView)
  },

  async activate(context: ToolContext) {
    setupMouseButtonLoadout(context, { middle: true })

    const classSelected = await preselectOrPromptForAnnotationClass(
      context.editor.activeView,
      ToolName.Skeleton,
      [annotationType],
      'You must create or select an existing Skeleton class before using the skeleton tool',
    )

    if (!classSelected) {
      return
    }

    const klass = context.editor.activeView.annotationManager.preselectedAnnotationClass

    if (klass?.skeletonMetaData?.mode === 'point_by_point') {
      this.isPointByPointMode = true

      if (klass) {
        this.pointByPointManager = new SkeletonPointByPointManager(klass)
        this.lastKnownSkeletonMetadata = JSON.stringify(klass.skeletonMetaData)
      }

      const skeletonToolState = useSkeletonToolState()
      if (this.pointByPointManager) {
        skeletonToolState.initializeManager(this.pointByPointManager)
      }

      ToolEvents.changed.emit({
        toolName: ToolName.Skeleton,
        lastHotkeyPressed: null,
        editing: false,
      })

      selectCursor(EditorCursor.Draw)
    } else {
      this.isPointByPointMode = false
      this.pointByPointManager = undefined
      this.lastKnownSkeletonMetadata = undefined
      selectCursor(EditorCursor.BBox)
    }

    context.editor.registerCommand('skeleton_tool.cancel', () => {
      if (this.isPointByPointMode && this.pointByPointManager) {
        const progressData = this.pointByPointManager.getProgressData()

        if (progressData.placedNodesCount !== 0) {
          ToolEvents.cancel.emit({
            toolName: ToolName.Skeleton,
            lastHotkeyPressed: null,
            editing: false,
          })

          this.reset(context)
          this.draw(context.editor.activeView)
        }

        return CallbackStatus.Stop
      }

      this.reset(context)
      this.draw(context.editor.activeView)
      return CallbackStatus.Stop
    })

    context.editor.registerCommand('skeleton_tool.submit', async () => {
      if (this.isPointByPointMode && this.pointByPointManager?.isComplete()) {
        ToolEvents.submit.emit(
          {
            toolName: ToolName.Skeleton,
            lastHotkeyPressed: null,
            editing: false,
          },
          {
            data: undefined,
            clicks: [],
          },
        )

        await this.savePointByPointSkeleton(context)
        return CallbackStatus.Stop
      }
      return CallbackStatus.Continue
    })

    context.handles.push(
      ...context.editor.onMouseDown((e) => {
        if (!isLeftMouseButton(e)) {
          return CallbackStatus.Continue
        }
        return this.onStart(context, e)
      }),
    )
    context.handles.push(...context.editor.onMouseMove((event) => this.onMove(context, event)))
    context.handles.push(...context.editor.onMouseUp((event) => this.onEnd(context, event)))

    context.handles.push(...context.editor.onTouchStart((event) => this.onStart(context, event)))
    context.handles.push(...context.editor.onTouchMove((event) => this.onMove(context, event)))
    context.handles.push(...context.editor.onTouchEnd((event) => this.onEnd(context, event)))

    if (this.isPointByPointMode) {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Backspace') {
          this.isBackspacePressed = true
        }
      }

      const onKeyUp = (event: KeyboardEvent) => {
        if (event.key === 'Backspace') {
          this.isBackspacePressed = false
        }
      }

      document.addEventListener('keydown', onKeyDown)
      document.addEventListener('keyup', onKeyUp)

      context.handles.push({
        id: Date.now(),
        release: () => {
          document.removeEventListener('keydown', onKeyDown)
          document.removeEventListener('keyup', onKeyUp)
        },
      })
    }

    const onClassChange = () => {
      this.switchMode(context)
    }

    EditorEvents.preselectedClassIdChanged.on(onClassChange)

    context.handles.push({
      id: Date.now(),
      release: () => {
        EditorEvents.preselectedClassIdChanged.off(onClassChange)
      },
    })
  },

  checkForClassMetadataChanges(view: View): void {
    if (!this.isPointByPointMode || !this.pointByPointManager) return

    const currentClass = view.annotationManager.preselectedAnnotationClass
    if (
      !currentClass?.skeletonMetaData ||
      currentClass.id !== this.pointByPointManager.getClassId()
    ) {
      return
    }

    const currentMetadata = JSON.stringify(currentClass.skeletonMetaData)
    if (this.lastKnownSkeletonMetadata && this.lastKnownSkeletonMetadata !== currentMetadata) {
      this.pointByPointManager.updateFromClass(currentClass)
    }

    this.lastKnownSkeletonMetadata = currentMetadata
  },

  draw(view: View): void {
    this.checkForClassMetadataChanges(view)

    view.annotationsLayer.draw((ctx) => {
      if (this.cursorPoint) {
        drawGuideLines(ctx, view, this.cursorPoint)
      }

      if (this.isPointByPointMode && this.pointByPointManager && this.cursorPoint) {
        const annotationClass = view.annotationManager.preselectedAnnotationClass

        if (annotationClass) {
          drawTemporarySkeletonPointByPoint(
            ctx,
            this.cursorPoint,
            annotationClass,
            this.pointByPointManager,
            view,
          )
        }

        return
      }

      if (this.cursorPoint == null || this.initialPoint == null) {
        return
      }

      ctx.beginPath()
      ctx.strokeStyle = view.annotationManager.preselectedAnnotationClassColor()
      ctx.lineWidth = 1
      ctx.strokeRect(
        this.initialPoint.x,
        this.initialPoint.y,
        this.cursorPoint.x - this.initialPoint.x,
        this.cursorPoint.y - this.initialPoint.y,
      )

      if (!view.annotationManager.preselectedAnnotationClass) {
        return
      }

      drawTemporarySkeletonV2(
        ctx,
        this.initialPoint,
        this.cursorPoint,
        view.annotationManager.preselectedAnnotationClass,
      )
    })
  },

  deactivate(context: ToolContext): void {
    if (this.pointByPointManager) {
      const skeletonToolState = useSkeletonToolState()
      skeletonToolState.cleanup()

      ToolEvents.cancel.emit({
        toolName: ToolName.Skeleton,
        lastHotkeyPressed: null,
        editing: false,
      })
    }

    context.editor.activeView.annotationsLayer.draw()
  },

  reset() {
    this.initialPoint = undefined
    this.cursorPoint = undefined

    if (this.pointByPointManager) {
      this.pointByPointManager.reset()
    }
  },
}
